import {
  idbReadwritePromise,
  resolveIdbDomError,
  toIndexedDbError,
  type IndexedDbOperationMeta,
} from '../errors/webStorageErrorReporting'

const DB_NAME = 'buxus-web-operator-files'
const STORE = 'files'
const DB_VERSION = 1
const STORE_NAME = 'operator-files' as const

export type StoredOperatorFile = {
  operatorVersion: string
  fileId: string
  ext: string
  blob: Blob
}

function fileKey(operatorId: string, fileId: string): string {
  return `v1:${operatorId}:${fileId}`
}

function operatorKeyPrefix(operatorId: string): string {
  return `v1:${operatorId}:`
}

function idbMeta(
  operation: IndexedDbOperationMeta['operation'],
  extra?: Omit<IndexedDbOperationMeta, 'store' | 'dbName' | 'operation'>,
): IndexedDbOperationMeta {
  return { store: STORE_NAME, dbName: DB_NAME, operation, ...extra }
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onerror = () => {
        reject(
          toIndexedDbError(req.error, 'IndexedDB open failed', idbMeta('open')),
        )
      }
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE)
        }
      }
      req.onsuccess = () => resolve(req.result)
    })
  }
  return dbPromise
}

export async function idbPutOperatorFile(
  operatorId: string,
  operatorVersion: string,
  fileId: string,
  ext: string,
  blob: Blob,
  options?: { ignored?: boolean },
): Promise<void> {
  const db = await openDb()
  const data: StoredOperatorFile = {
    operatorVersion,
    fileId,
    ext,
    blob,
  }
  await idbReadwritePromise(
    db,
    STORE,
    idbMeta('putFile', {
      operatorId,
      fileId,
      version: operatorVersion,
      blobSizeBytes: blob.size,
      ...(options?.ignored ? { ignored: true } : {}),
    }),
    'idb write',
    (store) => store.put(data, fileKey(operatorId, fileId)),
  )
}

export async function idbGetOperatorFile(
  operatorId: string,
  fileId: string,
  expectedOperatorVersion: string,
  options?: { ignored?: boolean },
): Promise<Blob | null> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(fileKey(operatorId, fileId))
    req.onsuccess = () => {
      const row = req.result as StoredOperatorFile | undefined
      if (!row || row.operatorVersion !== expectedOperatorVersion) {
        resolve(null)
        return
      }
      resolve(row.blob)
    }
    req.onerror = () => {
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb read',
          idbMeta('getFile', {
            operatorId,
            fileId,
            version: expectedOperatorVersion,
            ...(options?.ignored ? { ignored: true } : {}),
          }),
        ),
      )
    }
  })
}

export async function idbHasOperatorFile(
  operatorId: string,
  fileId: string,
  expectedOperatorVersion: string,
  options?: { ignored?: boolean },
): Promise<boolean> {
  try {
    const blob = await idbGetOperatorFile(
      operatorId,
      fileId,
      expectedOperatorVersion,
      options,
    )
    return blob != null
  } catch {
    // Treat cache-probe failures as a miss (especially when ignored during download).
    return false
  }
}

/** Delete cached blobs for one operator that are not in the current manifest. */
export async function idbPruneOperatorFiles(
  operatorId: string,
  keepFileIds: ReadonlySet<string>,
  options?: { ignored?: boolean },
): Promise<void> {
  const db = await openDb()
  const prefix = operatorKeyPrefix(operatorId)
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const store = tx.objectStore(STORE)
    const req = store.openKeyCursor()
    let settled = false
    const fail = () => {
      if (settled) return
      settled = true
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb prune',
          idbMeta('pruneFiles', {
            operatorId,
            ...(options?.ignored ? { ignored: true } : {}),
          }),
        ),
      )
    }
    tx.oncomplete = () => {
      if (settled) return
      settled = true
      resolve()
    }
    tx.onerror = fail
    tx.onabort = fail
    req.onsuccess = () => {
      const cursor = req.result
      if (!cursor) return
      const key = cursor.key
      if (typeof key === 'string' && key.startsWith(prefix)) {
        const fileId = key.slice(prefix.length)
        if (!keepFileIds.has(fileId)) {
          store.delete(key)
        }
      }
      cursor.continue()
    }
    req.onerror = fail
  })
}

export async function idbDeleteOperatorFiles(operatorId: string): Promise<void> {
  const db = await openDb()
  const prefix = operatorKeyPrefix(operatorId)
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const store = tx.objectStore(STORE)
    const req = store.openKeyCursor()
    let settled = false
    const fail = () => {
      if (settled) return
      settled = true
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb delete',
          idbMeta('deleteFiles', { operatorId }),
        ),
      )
    }
    tx.oncomplete = () => {
      if (settled) return
      settled = true
      resolve()
    }
    tx.onerror = fail
    tx.onabort = fail
    req.onsuccess = () => {
      const cursor = req.result
      if (!cursor) return
      const key = cursor.key
      if (typeof key === 'string' && key.startsWith(prefix)) {
        store.delete(key)
      }
      cursor.continue()
    }
    req.onerror = fail
  })
}

export async function idbClearOperatorFiles(): Promise<void> {
  const db = await openDb()
  await idbReadwritePromise(
    db,
    STORE,
    idbMeta('clearFiles'),
    'idb clear',
    (store) => store.clear(),
  )
}
